/**
 * src/app/app.module.ts
 *
 * @author Bryan Henry <bryan@payfully.co>
 * @date 4/27/2021
 * @copyright Payfully, 2021
 */

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { NgxUploaderModule } from 'ngx-uploader-esb';

import { initializeApp } from './app.initialize';
import { AppRoutingModule } from './app-routing.module';
import {
  ChangeClosingDateComponent,
  TotalByPromisedListPipe,
} from './components/change-closing-date/change-closing-date.component';
import { IsMoreDaysPipe } from './components/change-closing-date/is-more-days.pipe';
import { FooterComponent } from './components/footer/footer.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { ThemeSwitcherComponent } from './components/theme-switcher/theme-switcher.component';
import { DealService } from './modules/advance-application/services/deal.service';
import { AuthenticationModule } from './modules/authentication/authentication.module';
import { AuthInterceptor } from './modules/authentication/services/authentication.interceptor';
import { AuthenticationService } from './modules/authentication/services/authentication.service';
import { ConfirmModalComponent } from './modules/confirm-modal/confirm-modal.component';
import { ConfirmModalModule } from './modules/confirm-modal/confirm-modal.module';
import { IconsModule } from './modules/icons/icons.module';
import { InactiveSessionsDialogComponent } from './modules/inactive-sessions/components/inactive-sessions-dialog.component';
import { MaterialModule } from './modules/material';
import { PipesModule } from './modules/pipes/pipes.module';
import { ProfileService } from './modules/profile/services/profile.service';
import { AgreementService } from './modules/shared/services/agreement.service';
import { AlertsService } from './modules/shared/services/alerts.service';
import { DateService } from './modules/shared/services/date.service';
import { LocaliseService } from './modules/shared/services/localise.service';
import { UrlInterceptor } from './modules/shared/services/url.interceptor';
import { DashboardModule } from './pages/dashboard/dashboard.module';
import { ThemesService } from './services/themes.service';

const maskConfig: Partial<IConfig> = {
  validation: false,
};

@NgModule({
  declarations: [],
  imports: [
    AppRoutingModule,
    MaterialModule,
    AuthenticationModule,
    MatMenuModule,
    NgxMaskModule.forRoot(maskConfig),
    BrowserAnimationsModule,
    ConfirmModalModule,
    ConfirmModalComponent,
    ToastrModule.forRoot(),
    MatProgressBarModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    NgxUploaderModule,
    FormsModule,
    PipesModule,
    MatExpansionModule,
    MatToolbarModule,
    MatSidenavModule,
    MatSlideToggleModule,
    IconsModule,
    DashboardModule,
    FooterComponent,
    ThemeSwitcherComponent,
    ChangeClosingDateComponent,
    SidebarComponent,
    NavigationComponent,
    InactiveSessionsDialogComponent,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UrlInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [
        AuthenticationService,
        DealService,
        AlertsService,
        Router,
        AgreementService,
        ProfileService,
        DateService,
        ToastrService,
        MatDialog,
        LocaliseService,
        ThemesService,
      ],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
